import { z } from "zod";

/** API'de para: bigint JSON'da taşınamaz → en küçük birimde tam sayı metni (K-003; float'a asla düşmez). */
export const ApiMoney = z.object({
  amountMinor: z.string().regex(/^-?\d+$/),
  currency: z.string().length(3),
});
export type ApiMoney = z.infer<typeof ApiMoney>;
export const toApiMoney = (m: { amountMinor: bigint; currency: string }): ApiMoney => ({
  amountMinor: m.amountMinor.toString(),
  currency: m.currency,
});

/** POST /v1/tasks — form cevaplarıyla taslak iş (flows.md: draft) */
export const CreateTaskBody = z.object({
  market: z.string().min(2).max(8),
  service: z.string().min(1).max(80),
  /** soru kodu → cevap (şablona göre doğrulanır, core/form.ts) */
  answers: z.record(z.string(), z.unknown()),
  /** hizmet hem müşterinin hem hizmet verenin yerinde yapılabiliyorsa (K-061) */
  locationMode: z.enum(["customer", "provider"]).optional(),
  descriptionLocale: z.string().max(10).optional(),
  /** acil çağrı (K-050): yalnızca acil açık hizmetlerde; iş hemen, "şu an müsait" hizmet verenle */
  urgent: z.boolean().optional(),
});
export type CreateTaskBody = z.infer<typeof CreateTaskBody>;

/** Yerinde bulunacak kişi (başkası adına rezervasyon, K-070) */
export const OnsiteContact = z.object({
  name: z.string().trim().min(1).max(120),
  phone: z
    .string()
    .trim()
    .regex(/^\+?[0-9 ()-]{6,20}$/),
});

/** Kapı kodu, anahtar kutusu, park yeri… (K-056): şifreli saklanır, işe kısa süre kala görünür, iş bitince silinir */
export const AccessInstructions = z.string().trim().min(1).max(1000);

/** Formun cevabı, okunur haliyle (soru etiketi + değer; seçenekli soruda seçeneklerin etiketleri) */
export const TaskAnswerView = z.object({
  code: z.string(),
  label: z.string(),
  kind: z.string(),
  value: z.union([z.string(), z.number()]).nullable(),
  options: z.array(z.string()),
});
export type TaskAnswerView = z.infer<typeof TaskAnswerView>;

/** İş adresi. Kabulden önce hizmet verene yalnızca yerleşim yeri gider (K-061): line1/line2/postcode/konum null. */
export const TaskAddressView = z.object({
  role: z.enum(["location", "start", "end", "provider_place"]),
  locality: z.string().nullable(),
  line1: z.string().nullable(),
  line2: z.string().nullable(),
  postcode: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  locationNote: z.string().nullable(),
});
export type TaskAddressView = z.infer<typeof TaskAddressView>;

export const TaskSummary = z.object({
  id: z.string().uuid(),
  status: z.string(),
  service: z.string(),
  /** hizmetin çevirili adı (web/mobil başlık) */
  serviceName: z.string().optional(),
  /** seçilen hizmet veren (talep gönderildiyse) */
  provider: z
    .object({ displayName: z.string(), slug: z.string().nullable() })
    .nullable()
    .optional(),
  locationMode: z.enum(["customer", "provider"]),
  scheduledStart: z.string().nullable(),
  scheduledMinutes: z.number().nullable(),
  providerId: z.string().uuid().nullable(),
  /** müşterinin verdiği bilgiler (kendi işinde tam görünür) */
  description: z.string().nullable().optional(),
  answers: z.array(TaskAnswerView).optional(),
  addresses: z.array(TaskAddressView).optional(),
  accessInstructions: z.string().nullable().optional(),
  onsiteContact: OnsiteContact.nullable().optional(),
  /** K-051: hizmet veren yola çıktı / vardı */
  departedAt: z.string().nullable().optional(),
  arrivedAt: z.string().nullable().optional(),
  /** yalnızca müşteri görünümünde: talepte kullanılan kart */
  paymentMethodId: z.string().uuid().nullable().optional(),
  /** yalnızca müşteri görünümünde; ek süre / masraf / ek iş istekleri (K-053) */
  changeOrders: z.array(z.lazy(() => ChangeOrderView)).optional(),
  /** tarih değiştirme önerileri (K-113) */
  reschedules: z.array(z.lazy(() => RescheduleView)).optional(),
  /** sabit fiyatlı işte seçilen menü kalemleri (K-058); fiyatlar Poji ücreti dahil */
  menuItems: z.array(z.lazy(() => TaskMenuItemView)).optional(),
  pricing: z
    .object({
      pricingType: z.enum(["hourly", "fixed"]),
      customerHourlyPrice: ApiMoney.nullable(),
      minMinutes: z.number(),
      billingIncrementMinutes: z.number(),
      freeCancelHours: z.number(),
    })
    .nullable(),
});
export type TaskSummary = z.infer<typeof TaskSummary>;

/** GET /v1/tasks/:id/providers — "Browse Taskers & prices" (K-023) */
export const ProviderOffer = z.object({
  providerId: z.string().uuid(),
  displayName: z.string(),
  type: z.enum(["individual", "company"]),
  ratingAvg: z.number().nullable(),
  ratingCount: z.number(),
  /** müşterinin gördüğü, komisyon dahil saatlik fiyat (K-012) */
  hourlyPrice: ApiMoney.nullable(),
  minMinutes: z.number(),
  /** iş büyüklüğünden tahmini tutar (en az süre + 15 dk dilim) */
  estimatedTotal: ApiMoney.nullable(),
  /** sabit fiyatlı hizmette (K-058): menüdeki en düşük fiyat, Poji ücreti dahil ("€30'dan başlayan") */
  priceFrom: ApiMoney.nullable().optional(),
  /** bugün geçerli kampanya (K-016): tahmini toplamdaki indirim; estimatedTotal indirimli */
  promotion: z
    .object({ id: z.string().uuid(), name: z.string(), discount: ApiMoney })
    .nullable()
    .optional(),
  travelFee: ApiMoney.nullable(),
  slug: z.string().nullable(),
  completedTasks: z.number(),
  languages: z.array(z.string()),
  /** K-066: ilk işlerinde "Yeni" */
  isNew: z.boolean(),
  /** `at` verildiyse o saatte müsait mi; verilmediyse null */
  available: z.boolean().nullable(),
});
export const ProvidersResponse = z.object({
  providers: z.array(ProviderOffer),
  /** acil işte (K-050): kabul anında çekilecek acil ücreti */
  urgentFee: ApiMoney.nullable().optional(),
  /** girilen kampanya kodu geçersizse nedeni (K-016) */
  promoError: z.string().nullable().optional(),
});
export type ProvidersResponse = z.infer<typeof ProvidersResponse>;

/** GET /v1/tasks/:id/providers sorgusu (K-093): sıralama ve filtreler */
const MinorQ = z.string().regex(/^\d{1,9}$/);
export const ProvidersQuery = z.object({
  sort: z
    .enum(["recommended", "rating", "price_asc", "price_desc", "most_tasks", "earliest"])
    .default("recommended"),
  minRating: z.coerce.number().min(1).max(5).optional(),
  minPrice: MinorQ.optional(),
  maxPrice: MinorQ.optional(),
  language: z
    .string()
    .regex(/^[a-z]{2}$/)
    .optional(),
  type: z.enum(["individual", "company"]).optional(),
  new: z.enum(["true", "false"]).optional(),
  /** bu saatte müsait olanlar (müsaitlik hesaplanır); available=true ile yalnızca müsaitler */
  at: z.string().datetime({ offset: true }).optional(),
  available: z.enum(["true", "false"]).optional(),
  /** kampanya kodu önizlemesi (K-016) */
  promoCode: z.string().trim().min(2).max(32).optional(),
});
export type ProvidersQuery = z.infer<typeof ProvidersQuery>;

/** POST /v1/tasks/:id/request — hizmet veren + zaman seçilir, fiyat kopyalanır (draft → requested) */

export const RequestTaskBody = z.object({
  /** isteğe bağlı; sonra PUT /v1/tasks/:id/access ile de verilebilir */
  accessInstructions: AccessInstructions.optional(),
  onsiteContact: OnsiteContact.optional(),
  providerId: z.string().uuid(),
  /** normal işte zorunlu; acil işte verilmez (iş hemen başlar) */
  scheduledStart: z.string().datetime({ offset: true }).optional(),
  /** kayıtlı kart (K-017): provizyon ve tahsilat bu karttan */
  paymentMethodId: z.string().uuid(),
  /** sabit fiyatlı hizmette (K-058) hizmet verenin menüsünden seçilen kalemler */
  menuItems: z
    .array(z.object({ id: z.string().uuid(), quantity: z.number().int().min(1).max(5).default(1) }))
    .min(1)
    .max(10)
    .optional(),
  /** kampanya kodu (K-016); kodsuz kampanyalar kendiliğinden uygulanır */
  promoCode: z.string().trim().min(2).max(32).optional(),
});
export type RequestTaskBody = z.infer<typeof RequestTaskBody>;

/** PUT /v1/tasks/:id/access — müşteri erişim notunu ve yerindeki kişiyi işe kadar değiştirebilir; null = sil */
export const UpdateTaskAccessBody = z
  .object({
    accessInstructions: AccessInstructions.nullable(),
    onsiteContact: OnsiteContact.nullable(),
  })
  .partial()
  .refine((b) => Object.keys(b).length > 0, { message: "empty" });
export type UpdateTaskAccessBody = z.infer<typeof UpdateTaskAccessBody>;

/** Hizmet verenin gördüğü iş (GET /v1/provider/tasks/:id) */
export const ProviderTaskDetail = z.object({
  id: z.string().uuid(),
  status: z.string(),
  service: z.string(),
  /** hizmetin çevirili adı */
  serviceName: z.string().optional(),
  isUrgent: z.boolean(),
  locationMode: z.enum(["customer", "provider"]),
  scheduledStart: z.string().nullable(),
  scheduledMinutes: z.number().nullable(),
  description: z.string().nullable(),
  /** müşterinin yalnızca adı (soyadının baş harfi) */
  customerName: z.string().nullable(),
  answers: z.array(TaskAnswerView),
  addresses: z.array(TaskAddressView),
  /** tam adres görünüyor mu (kabulden sonra) */
  fullAddress: z.boolean(),
  /** erişim notu: işe market_settings.tracking_lead_minutes kala görünür; öncesinde `accessAvailableAt` */
  accessInstructions: z.string().nullable(),
  accessAvailableAt: z.string().nullable(),
  onsiteContact: OnsiteContact.nullable(),
  /** müşterinin talep fotoğrafları (GET /v1/tasks/:id/media ile açılır) */
  requestPhotoCount: z.number(),
  /** K-051: "yola çıktım" / "vardım" zamanları */
  departedAt: z.string().nullable(),
  arrivedAt: z.string().nullable(),
  /** K-116: müşteri ücretsiz iptal istedi / hizmet veren ücretten vazgeçti */
  cancelWaiverRequestedAt: z.string().nullable(),
  cancelFeeWaivedAt: z.string().nullable(),
  hourlyRate: ApiMoney.nullable(),
  changeOrders: z.array(z.lazy(() => ChangeOrderView)),
  reschedules: z.array(z.lazy(() => RescheduleView)),
  /** sabit fiyatlı iş (K-058): seçilen kalemler, hizmet veren fiyatıyla */
  menuItems: z.array(z.lazy(() => TaskMenuItemView)),
});
export type ProviderTaskDetail = z.infer<typeof ProviderTaskDetail>;

/** POST /v1/tasks/:id/cancel ve /v1/provider/tasks/:id/cancel (K-022) */
export const CancelTaskBody = z.object({
  reasonCode: z
    .enum([
      "schedule",
      "no_longer_needed",
      "found_other",
      "wrong_details",
      "provider_unavailable",
      "other",
    ])
    .optional(),
  reasonText: z.string().max(500).optional(),
});
export type CancelTaskBody = z.infer<typeof CancelTaskBody>;

/** GET /v1/tasks/:id/cancellation — iptal etmeden önce sonucu gösterir */
export interface CancellationView {
  cancellable: boolean;
  free: boolean;
  fee: ApiMoney | null;
  /** ücretsiz iptalin son anı (varsa) */
  freeUntil: string | null;
  /** acil işte (K-050): kabul edildiyse acil ücreti iade edilir mi (hizmet veren geç kaldıysa evet) */
  urgentFeeRefunded?: boolean;
  /** K-116: geç iptal neden ücretsiz (hizmet veren vazgeçti / saat değişikliğini hizmet veren istedi) */
  waived?: "provider_waiver" | "provider_reschedule" | null;
  /** K-116: müşteri ücretsiz iptal istedi, hizmet veren yanıtı bekleniyor */
  waiverRequestedAt?: string | null;
}

/** POST /v1/provider/tasks/:id/change-orders — ek süre / masraf / ek iş isteği (K-053) */
export const CreateChangeOrderBody = z
  .object({
    kind: z.enum(["extra_time", "expense", "extra_work"]),
    description: z.string().trim().min(3).max(500),
    /** ek süre (dk); yalnızca extra_time */
    extraMinutes: z
      .number()
      .int()
      .min(15)
      .max(8 * 60)
      .optional(),
    /** hizmet veren tutarı (kuruş); masraf ve ek işte zorunlu */
    amountMinor: z
      .string()
      .regex(/^\d{1,9}$/)
      .optional(),
    /** isteğe bağlı fotoğraf (task_media, stage = change_order) */
    mediaId: z.string().uuid().optional(),
  })
  .superRefine((b, ctx) => {
    if (b.kind === "extra_time" && b.extraMinutes === undefined)
      ctx.addIssue({ code: "custom", path: ["extraMinutes"], message: "required" });
    if (b.kind !== "extra_time" && b.amountMinor === undefined)
      ctx.addIssue({ code: "custom", path: ["amountMinor"], message: "required" });
  });
export type CreateChangeOrderBody = z.infer<typeof CreateChangeOrderBody>;

/** İş ayrıntısında ek istekler; müşteri tutarı Poji ücreti dahil (masrafta ücret yok) */
export const ChangeOrderView = z.object({
  id: z.string().uuid(),
  kind: z.enum(["extra_time", "expense", "extra_work"]),
  description: z.string(),
  extraMinutes: z.number().nullable(),
  providerAmount: ApiMoney,
  customerAmount: ApiMoney,
  status: z.enum(["pending", "approved", "rejected", "expired", "withdrawn"]),
  mediaPath: z.string().nullable(),
  createdAt: z.string(),
  respondedAt: z.string().nullable(),
});
export type ChangeOrderView = z.infer<typeof ChangeOrderView>;

/** POST /v1/tasks/:id/reschedules — tarih değiştirme önerisi (K-113); iki taraf da önerir */
export const ProposeRescheduleBody = z.object({
  newStart: z.string().datetime({ offset: true }),
  note: z.string().trim().max(300).optional(),
});
export type ProposeRescheduleBody = z.infer<typeof ProposeRescheduleBody>;

export const RescheduleView = z.object({
  id: z.string().uuid(),
  proposedBy: z.enum(["customer", "provider"]),
  oldStart: z.string().nullable(),
  newStart: z.string(),
  status: z.enum(["proposed", "accepted", "declined", "withdrawn", "expired"]),
  note: z.string().nullable(),
  createdAt: z.string(),
  respondedAt: z.string().nullable(),
});
export type RescheduleView = z.infer<typeof RescheduleView>;

/** Menü kalemi; müşteri görünümünde `price` Poji ücreti dahil (K-012) */
export const MenuItemView = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  price: ApiMoney,
  durationMinutes: z.number(),
});
export type MenuItemView = z.infer<typeof MenuItemView>;

/** İşte seçilen kalemler (anlık görüntü) */
export const TaskMenuItemView = z.object({
  name: z.string(),
  quantity: z.number(),
  unitPrice: ApiMoney,
  durationMinutes: z.number(),
});
export type TaskMenuItemView = z.infer<typeof TaskMenuItemView>;

/** GET /v1/tasks — müşterinin işleri (web "Görevlerim", K-122/K-123) */
export const MyTasksQuery = z.object({
  scope: z.enum(["upcoming", "past"]).default("upcoming"),
  locale: z
    .string()
    .regex(/^[a-z]{2}$/)
    .optional(),
});

export interface MyTaskItem {
  id: string;
  status: string;
  service: { code: string; name: string };
  provider: { displayName: string; slug: string | null } | null;
  scheduledStart: string | null;
  scheduledMinutes: number | null;
  locality: string | null;
  customerHourlyPrice: ApiMoney | null;
  createdAt: string;
}

export interface MyTasksResponse {
  tasks: MyTaskItem[];
}

/** PUT /v1/provider/tasks/:id/location (K-051): yoldaki hizmet verenin son konumu; rota geçmişi saklanmaz */
export const LiveLocationBody = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  heading: z.number().min(0).max(360).optional(),
  accuracyM: z.number().min(0).max(100000).optional(),
  etaMinutes: z.number().int().min(0).max(600).optional(),
});
export type LiveLocationBody = z.infer<typeof LiveLocationBody>;

/** GET /v1/tasks/:id/live-location: müşterinin yoldaki hizmet vereni görmesi */
export interface LiveLocationView {
  /** hizmet veren konum paylaşıyor mu (yola çıktı, henüz varmadı, konum gönderdi) */
  sharing: boolean;
  departedAt: string | null;
  arrivedAt: string | null;
  location: {
    latitude: number;
    longitude: number;
    heading: number | null;
    etaMinutes: number | null;
    updatedAt: string;
    /** son konum 2 dakikadan eski */
    stale: boolean;
  } | null;
  /** müşterinin kendi adresi (haritada hedef) */
  destination: { latitude: number; longitude: number } | null;
}
